import { useState } from 'react'
import { Ticket } from '../types'
import { highlightTextMultiple } from '../utils/textHighlight'
import { downloadFile } from '../utils/fileDownload'
import { useAlert } from '../contexts/AlertContext'
import { DetailsModal } from './DetailsModal'
import './TicketList.css'

interface TicketListProps {
  tickets: Ticket[]
  onAssignExecutor?: (ticket: Ticket) => void
  onComplete?: (ticketId: string) => void
  onAccelerate?: (ticketId: string) => void
  onDelete?: (ticketId: string) => void
  onAddComment?: (ticketId: string, comment: string) => void
  showAssignButton?: boolean
  showCompleteButton?: boolean
  showAccelerateButton?: boolean
  showDeleteButton?: boolean
  showAddCommentButton?: boolean
  searchText?: string
  highlightedTicketId?: string | null
}

function TicketList({ 
  tickets, 
  onAssignExecutor, 
  onComplete,
  onAccelerate,
  onDelete,
  onAddComment,
  showAssignButton = false,
  showCompleteButton = false,
  showAccelerateButton = false,
  showDeleteButton = false,
  showAddCommentButton = false,
  searchText = '',
  highlightedTicketId = null,
}: TicketListProps) {
  const { showAlert } = useAlert()
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false)
  const [commentText, setCommentText] = useState('')
  const [ticketForComment, setTicketForComment] = useState<Ticket | null>(null)
  if (tickets.length === 0) {
    return (
      <div className="ticket-list-empty">
        <p>Žádné žádosti</p>
      </div>
    )
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'new':
        return 'Nový'
      case 'in_progress':
        return 'V řešení'
      case 'completed':
        return 'Dokončeno'
      default:
        return status
    }
  }

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'new':
        return 'status-new'
      case 'in_progress':
        return 'status-in-progress'
      case 'completed':
        return 'status-completed'
      default:
        return ''
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('cs-CZ', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const handleCardClick = (ticket: Ticket) => {
    setSelectedTicket(ticket)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedTicket(null)
  }

  const handleOpenCommentModal = (ticket: Ticket, e: React.MouseEvent) => {
    e.stopPropagation()
    setTicketForComment(ticket)
    setCommentText('')
    setIsCommentModalOpen(true)
  }

  const handleCloseCommentModal = () => {
    setIsCommentModalOpen(false)
    setTicketForComment(null)
    setCommentText('')
  }

  const handleSubmitComment = async () => {
    if (!ticketForComment || !commentText.trim()) {
      showAlert('Zadejte komentář', 'error')
      return
    }

    if (onAddComment) {
      try {
        await onAddComment(ticketForComment.id, commentText.trim())
        handleCloseCommentModal()
        showAlert('Komentář byl přidán', 'success')
      } catch (error: any) {
        showAlert('Chyba při přidávání komentáře: ' + (error.message || 'Neznámá chyba'), 'error')
      }
    }
  }

  return (
    <>
    <div className="ticket-list">
      {tickets.map(ticket => (
        <div 
          key={ticket.id}
          id={`ticket-${ticket.id}`}
          className={`ticket-card ${ticket.admin_type === 'spravce_budov' ? 'ticket-building-admin' : ''}`}
          style={{
            ...(ticket.admin_type === 'spravce_budov' ? { backgroundColor: '#fff9e6', borderLeft: '4px solid #ffc107' } : {}),
            ...(highlightedTicketId === ticket.id
              ? { outline: '2px solid #0d9488', outlineOffset: '2px', backgroundColor: 'rgba(20, 184, 166, 0.12)' }
              : {}),
            cursor: 'pointer'
          }}
          onClick={() => handleCardClick(ticket)}
        >
          <div className="ticket-header">
            <div className="ticket-id">Žádost #{ticket.id}</div>
            <span className={`status-badge ${getStatusClass(ticket.status)}`}>
              {highlightTextMultiple(getStatusLabel(ticket.status), searchText)}
            </span>
          </div>

          <div className="ticket-info">
            <div className="info-row">
              <span className="info-label">Budova:</span>
              <span className="info-value">{highlightTextMultiple(ticket.building_name || 'Neznámá', searchText)}</span>
            </div>
            
            <div className="info-row">
              <span className="info-label">Žadatel:</span>
              <span className="info-value">
                {highlightTextMultiple(ticket.requester_name || 'Neznámý', searchText)}
                {ticket.requester_email && (
                  <div>
                    <a href={`mailto:${ticket.requester_email}`} className="email-link">
                      {highlightTextMultiple(ticket.requester_email, searchText)}
                    </a>
                  </div>
                )}
              </span>
            </div>

            <div className="info-row">
              <span className="info-label">Telefon:</span>
              <span className="info-value">
                {ticket.phone ? (
                  <a href={`tel:${ticket.phone}`} className="phone-link">
                    {highlightTextMultiple(ticket.phone, searchText)}
                  </a>
                ) : (
                  '-'
                )}
              </span>
            </div>

            {ticket.executor_name && (
              <div className="info-row">
                <span className="info-label">Řešitel:</span>
                <span className="info-value">{highlightTextMultiple(ticket.executor_name, searchText)}</span>
              </div>
            )}

            <div className="info-row">
              <span className="info-label">Vytvořeno:</span>
              <span className="info-value">
                {new Date(ticket.created_at).toLocaleString('cs-CZ', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>

            {ticket.assigned_at && (
              <div className="info-row">
                <span className="info-label">Přiřazeno:</span>
                <span className="info-value">
                  {new Date(ticket.assigned_at).toLocaleString('cs-CZ', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
            )}

            {ticket.completed_at && (
              <div className="info-row">
                <span className="info-label">Dokončeno:</span>
                <span className="info-value">
                  {new Date(ticket.completed_at).toLocaleString('cs-CZ', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
            )}

            {ticket.completion_comment && (
              <div className="info-row">
                <span className="info-label">Komentář k dokončení:</span>
                <span className="info-value" style={{ whiteSpace: 'pre-wrap' }}>
                  {highlightTextMultiple(ticket.completion_comment, searchText)}
                </span>
              </div>
            )}
          </div>

          <div className="ticket-description">
            <strong>Popis:</strong>
            <p>{highlightTextMultiple(ticket.description, searchText)}</p>
          </div>

          {ticket.file_name && ticket.file_url && (
            <div className="ticket-file">
              <a
                href="#"
                onClick={async (e) => {
                  e.preventDefault()
                  try {
                    await downloadFile(ticket.file_url || ticket.file_name || '', ticket.id, undefined, false)
                  } catch (error: any) {
                    showAlert('Chyba při otevírání souboru: ' + (error.message || 'Neznámá chyba'), 'error')
                  }
                }}
                className="file-link"
                title={`Otevřít přílohu: ${ticket.file_name}`}
              >
                📎 Příloha: {ticket.file_name}
              </a>
              <span style={{ marginLeft: '10px', fontSize: '0.9em' }}>
                <a
                  href="#"
                  onClick={async (e) => {
                    e.preventDefault()
                    try {
                      await downloadFile(ticket.file_url || ticket.file_name || '', ticket.id, undefined, true)
                    } catch (error: any) {
                      showAlert('Chyba při stahování souboru: ' + (error.message || 'Neznámá chyba'), 'error')
                    }
                  }}
                  className="file-link"
                  title="Stáhnout soubor"
                >
                  ⬇️ Stáhnout
                </a>
              </span>
            </div>
          )}

          <div className="ticket-actions" onClick={(e) => e.stopPropagation()}>
            {showAddCommentButton && onAddComment && (ticket.status === 'new' || ticket.status === 'in_progress') && (
              <button
                className="comment-button"
                onClick={(e) => handleOpenCommentModal(ticket, e)}
                style={{
                  backgroundColor: '#17a2b8',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  marginRight: '0.5rem'
                }}
              >
                💬 Přidat komentář
              </button>
            )}

            {showAssignButton && onAssignExecutor && (ticket.status === 'new' || ticket.status === 'in_progress') && (
              <button
                className={ticket.status === 'new' ? 'reassign-button' : 'assign-button'}
                onClick={(e) => {
                  e.stopPropagation()
                  onAssignExecutor(ticket)
                }}
              >
                {ticket.status === 'new' ? 'Přiřadit řešitele' : 'Změnit řešitele'}
              </button>
            )}

            {showCompleteButton && onComplete && ticket.status === 'in_progress' && (
              <button
                className="complete-button"
                onClick={(e) => {
                  e.stopPropagation()
                  onComplete(ticket.id)
                }}
              >
                HOTOVO
              </button>
            )}

            {showAccelerateButton && onAccelerate && ticket.status !== 'completed' && (
              <button
                className="accelerate-button"
                onClick={(e) => {
                  e.stopPropagation()
                  onAccelerate(ticket.id)
                }}
                title="Požádat o urychlení"
              >
                ⚡ Urychlit
              </button>
            )}

            {showDeleteButton && onDelete && (
              <button
                className="delete-button"
                onClick={(e) => {
                  e.stopPropagation()
                  if (confirm(`Opravdu chcete smazat žádost #${ticket.id}?`)) {
                    onDelete(ticket.id)
                  }
                }}
                style={{
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  marginLeft: '0.5rem',
                  fontSize: '0.9rem'
                }}
              >
                🗑️ Smazat
              </button>
            )}
          </div>
        </div>
      ))}
    </div>

    {selectedTicket && (
      <DetailsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={`Žádost #${selectedTicket.id}`}
        actions={
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {showAssignButton && onAssignExecutor && (selectedTicket.status === 'new' || selectedTicket.status === 'in_progress') && (
              <button
                className={selectedTicket.status === 'new' ? 'reassign-button' : 'assign-button'}
                onClick={() => {
                  onAssignExecutor(selectedTicket)
                  handleCloseModal()
                }}
              >
                {selectedTicket.status === 'new' ? 'Přiřadit řešitele' : 'Změnit řešitele'}
              </button>
            )}

            {showCompleteButton && onComplete && selectedTicket.status === 'in_progress' && (
              <button
                className="complete-button"
                onClick={() => {
                  onComplete(selectedTicket.id)
                  handleCloseModal()
                }}
              >
                HOTOVO
              </button>
            )}

            {showAccelerateButton && onAccelerate && selectedTicket.status !== 'completed' && (
              <button
                className="accelerate-button"
                onClick={() => {
                  onAccelerate(selectedTicket.id)
                  handleCloseModal()
                }}
                title="Požádat o urychlení"
              >
                ⚡ Urychlit
              </button>
            )}

            {showDeleteButton && onDelete && (
              <button
                className="delete-button"
                onClick={() => {
                  if (confirm(`Opravdu chcete smazat žádost #${selectedTicket.id}?`)) {
                    onDelete(selectedTicket.id)
                    handleCloseModal()
                  }
                }}
                style={{
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.9rem'
                }}
              >
                🗑️ Smazat
              </button>
            )}
          </div>
        }
      >
        <div className="details-section">
          <div className="details-section-title">Základní informace</div>
          <div className="details-grid">
            <div className="details-item">
              <div className="details-label">Status</div>
              <div className="details-value">
                <span className={`status-badge ${getStatusClass(selectedTicket.status)}`}>
                  {getStatusLabel(selectedTicket.status)}
                </span>
              </div>
            </div>
            <div className="details-item">
              <div className="details-label">Budova</div>
              <div className="details-value">{selectedTicket.building_name || 'Neznámá'}</div>
            </div>
            <div className="details-item">
              <div className="details-label">Žadatel</div>
              <div className="details-value">
                <strong>{selectedTicket.requester_name || 'Neznámý'}</strong>
                {selectedTicket.requester_email && (
                  <div>
                    <a href={`mailto:${selectedTicket.requester_email}`} className="email-link">
                      {selectedTicket.requester_email}
                    </a>
                  </div>
                )}
              </div>
            </div>
            {selectedTicket.phone && (
              <div className="details-item">
                <div className="details-label">Telefon</div>
                <div className="details-value">
                  <a href={`tel:${selectedTicket.phone}`} className="phone-link">
                    {selectedTicket.phone}
                  </a>
                </div>
              </div>
            )}
            {selectedTicket.executor_name && (
              <div className="details-item">
                <div className="details-label">Řešitel</div>
                <div className="details-value">{selectedTicket.executor_name}</div>
              </div>
            )}
          </div>
        </div>
        <div className="details-section">
          <div className="details-section-title">Popis</div>
          <div className="details-value" style={{ whiteSpace: 'pre-wrap' }}>
            {selectedTicket.description || '-'}
          </div>
        </div>
        {selectedTicket.file_name && selectedTicket.file_url && (
          <div className="details-section">
            <div className="details-section-title">Příloha</div>
            <div className="details-value">
              <a
                href="#"
                onClick={async (e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  try {
                    await downloadFile(selectedTicket.file_url || selectedTicket.file_name || '', selectedTicket.id, undefined, false)
                  } catch (error: any) {
                    showAlert('Chyba při otevírání souboru: ' + (error.message || 'Neznámá chyba'), 'error')
                  }
                }}
                className="file-link"
              >
                📎 {selectedTicket.file_name}
              </a>
              <span style={{ marginLeft: '10px', fontSize: '0.9em' }}>
                <a
                  href="#"
                  onClick={async (e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    try {
                      await downloadFile(selectedTicket.file_url || selectedTicket.file_name || '', selectedTicket.id, undefined, true)
                    } catch (error: any) {
                      showAlert('Chyba při stahování souboru: ' + (error.message || 'Neznámá chyba'), 'error')
                    }
                  }}
                  className="file-link"
                >
                  ⬇️ Stáhnout
                </a>
              </span>
            </div>
          </div>
        )}
        <div className="details-section">
          <div className="details-section-title">Časové údaje</div>
          <div className="details-grid">
            <div className="details-item">
              <div className="details-label">Vytvořeno</div>
              <div className="details-value">{formatDate(selectedTicket.created_at)}</div>
            </div>
            {selectedTicket.assigned_at && (
              <div className="details-item">
                <div className="details-label">Přiřazeno</div>
                <div className="details-value">{formatDate(selectedTicket.assigned_at)}</div>
              </div>
            )}
            {selectedTicket.completed_at && (
              <div className="details-item">
                <div className="details-label">Dokončeno</div>
                <div className="details-value">{formatDate(selectedTicket.completed_at)}</div>
              </div>
            )}
          </div>
        </div>
        {selectedTicket.completion_comment ? (
          <div className="details-section">
            <div className="details-section-title">Komentář k dokončení</div>
            <div className="details-value" style={{ whiteSpace: 'pre-wrap' }}>
              {selectedTicket.completion_comment}
            </div>
          </div>
        ) : null}
      </DetailsModal>
    )}

    {/* Модальное окно для добавления комментария */}
    {isCommentModalOpen && ticketForComment && (
      <DetailsModal
        isOpen={isCommentModalOpen}
        onClose={handleCloseCommentModal}
        title={`Přidat komentář k žádosti #${ticketForComment.id}`}
        actions={
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={handleCloseCommentModal}
              style={{
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Zrušit
            </button>
            <button
              onClick={handleSubmitComment}
              disabled={!commentText.trim()}
              style={{
                backgroundColor: '#17a2b8',
                color: 'white',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                cursor: commentText.trim() ? 'pointer' : 'not-allowed',
                opacity: commentText.trim() ? 1 : 0.5
              }}
            >
              💬 Přidat komentář
            </button>
          </div>
        }
      >
        <div className="details-section">
          <div className="details-section-title">Váš komentář</div>
          <textarea
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Zadejte komentář k žádosti..."
            style={{
              width: '100%',
              minHeight: '150px',
              padding: '0.75rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '1rem',
              fontFamily: 'inherit',
              resize: 'vertical'
            }}
            autoFocus
          />
          <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#666' }}>
            Komentář bude přidán k popisu žádosti s vaším jménem a časem.
          </div>
        </div>
      </DetailsModal>
    )}
    </>
  )
}

export default TicketList



